import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, DatePicker, Skeleton, Tooltip } from "antd";
import {
  CalendarOutlined,
  ExperimentOutlined,
  AimOutlined,
  TrophyOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Title } from "components/common";
import { useGetDashboardV2ActivityHeatmapQuery } from "../../api";
import { DASHBOARD_USE_MOCK_DATA } from "utils/featureFlags";
import { mockActivityHeatmap } from "../../mockData";
import styles from "./DashboardActivityHeatmap.module.less";

const { RangePicker } = DatePicker;

// ── Constants ─────────────────────────────────────────────────────────────────
const COLOR_SCALE = [
  { bg: "rgba(0,0,0,0.06)", label: "No activity" },
  { bg: "#fca5a5",          label: "< 40%"       },
  { bg: "#fdba74",          label: "40–60%"      },
  { bg: "#86efac",          label: "60–75%"      },
  { bg: "#4ade80",          label: "75–90%"      },
  { bg: "#16a34a",          label: "90%+"        },
];

const DATE_PRESETS = {
  "Last 30 Days":  [moment().subtract(29, "days").startOf("day"), moment().endOf("day")],
  "Last 3 Months": [moment().subtract(3, "months").startOf("day"), moment().endOf("day")],
  "Last 6 Months": [moment().subtract(6, "months").startOf("day"), moment().endOf("day")],
  "Last Year":     [moment().subtract(1, "year").startOf("day"), moment().endOf("day")],
};

// Mon=show, Tue=blank, Wed=show, Thu=blank, Fri=show, Sat=blank, Sun=show
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CELL_TOOLTIP = {
  color: "#fff",
  overlayInnerStyle: {
    background: "#fff",
    color: "#334155",
    borderRadius: 10,
    padding: "9px 13px",
    fontSize: 12,
    lineHeight: 1.6,
    boxShadow: "0 6px 24px rgba(0,0,0,0.10)",
    border: "1px solid #e6f0ff",
  },
  overlayStyle: { maxWidth: 220 },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const getDayColor = (quizCount, avgAccuracy) => {
  if (!quizCount || avgAccuracy === null) return "rgba(0,0,0,0.06)";
  if (avgAccuracy < 40)  return "#fca5a5";
  if (avgAccuracy < 60)  return "#fdba74";
  if (avgAccuracy < 75)  return "#86efac";
  if (avgAccuracy < 90)  return "#4ade80";
  return "#16a34a";
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("default", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
};

// Always builds a 52-week grid (no empty space).
// `rangeStartStr` / `rangeEndStr` are used only to dim out-of-range cells and compute stats.
const buildHeatmap = (activityDays = [], rangeStartStr, rangeEndStr) => {
  const activityMap = {};
  activityDays.forEach((d) => { activityMap[d.date] = d; });

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // Always start exactly 52 weeks back, aligned to Monday
  const gridStart = new Date();
  gridStart.setDate(gridStart.getDate() - 52 * 7);
  const dow = gridStart.getDay();
  gridStart.setDate(gridStart.getDate() + (dow === 0 ? -6 : 1 - dow));
  gridStart.setHours(0, 0, 0, 0);

  const weeks       = [];
  const monthLabels = [];
  const seenMonths  = new Set();
  let cur    = new Date(gridStart);
  let colIdx = 0;

  while (cur <= today) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const isInFuture = cur > today;
      const mm      = String(cur.getMonth() + 1).padStart(2, "0");
      const dd      = String(cur.getDate()).padStart(2, "0");
      const dateStr = `${cur.getFullYear()}-${mm}-${dd}`;

      if (d === 0 && !isInFuture) {
        const key = `${cur.getFullYear()}-${cur.getMonth()}`;
        if (!seenMonths.has(key)) {
          seenMonths.add(key);
          monthLabels.push({ label: cur.toLocaleString("default", { month: "short" }), col: colIdx });
        }
      }

      const inRange = (!rangeStartStr || dateStr >= rangeStartStr) &&
                      (!rangeEndStr   || dateStr <= rangeEndStr);
      const activity = isInFuture ? null : activityMap[dateStr];

      week.push({
        dateStr,
        quizCount:   activity?.quizCount   ?? 0,
        avgAccuracy: activity?.avgAccuracy ?? null,
        isFuture: isInFuture,
        inRange,
      });
      cur.setDate(cur.getDate() + 1);
    }
    weeks.push(week);
    colIdx++;
  }

  // Stats only over the selected range
  const rangeData = activityDays.filter((d) => {
    return (!rangeStartStr || d.date >= rangeStartStr) &&
           (!rangeEndStr   || d.date <= rangeEndStr);
  });

  const totalActive  = rangeData.length;
  const totalQuizzes = rangeData.reduce((s, d) => s + (d.quizCount || 0), 0);
  const avgAccuracy  = totalActive > 0
    ? parseFloat((rangeData.reduce((s, d) => s + (d.avgAccuracy || 0), 0) / totalActive).toFixed(1))
    : null;
  const bestDay = totalActive > 0
    ? rangeData.reduce((b, d) => (d.avgAccuracy || 0) > (b.avgAccuracy || 0) ? d : b, rangeData[0])
    : null;

  return { weeks, monthLabels, stats: { totalActive, totalQuizzes, avgAccuracy, bestDay } };
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
const HeatmapSkeleton = () => (
  <Card className={styles.heatmapCard}>
    <Skeleton.Input active size="small" style={{ width: 180, marginBottom: 16 }} />
    <Skeleton active title={false} paragraph={{ rows: 8, width: "100%" }} />
  </Card>
);

// ── Main component ────────────────────────────────────────────────────────────
const DashboardActivityHeatmap = () => {
  const user   = useSelector((state) => state?.app?.user?.info);
  const userId = user?.user_code ?? user?._id;

  // Default: last year
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "year").startOf("day"),
    moment().endOf("day"),
  ]);

  const rangeStartStr = dateRange?.[0]?.format("YYYY-MM-DD") ?? null;
  const rangeEndStr   = dateRange?.[1]?.format("YYYY-MM-DD") ?? null;

  const { data, isLoading, isFetching } = useGetDashboardV2ActivityHeatmapQuery(
    { userId },
    { skip: !userId || DASHBOARD_USE_MOCK_DATA, refetchOnMountOrArgChange: true }
  );

  const activityDays = DASHBOARD_USE_MOCK_DATA
    ? mockActivityHeatmap
    : (data?.activityDays ?? []);

  const { weeks, monthLabels, stats } = useMemo(
    () => buildHeatmap(activityDays, rangeStartStr, rangeEndStr),
    [activityDays, rangeStartStr, rangeEndStr]
  );

  if (isLoading && !activityDays.length) return <HeatmapSkeleton />;

  const monthLabelMap = {};
  monthLabels.forEach((m) => { monthLabelMap[m.col] = m.label; });

  return (
    <Card className={styles.heatmapCard}>
      <div className={styles.cardContentWrap}>

        {/* ── Header ── */}
        <div className={styles.sectionHeader}>
          <Title level={5} className={styles.sectionTitle}>
            Activity Heatmap
          </Title>

          <div className={styles.headerRight}>
            {/* Date range filter */}
            <div className={styles.filterGroup}>
              <Tooltip title="Filter stats by date range" placement="topRight">
                <FilterOutlined className={styles.filterIcon} />
              </Tooltip>
              <RangePicker
                size="small"
                value={dateRange}
                ranges={DATE_PRESETS}
                format="MMM D, YYYY"
                allowClear
                onChange={(val) => setDateRange(val)}
                placeholder={["From", "To"]}
              />
            </div>

            {/* Color legend */}
            <div className={styles.legend}>
              <span className={styles.legendText}>Less</span>
              {COLOR_SCALE.map((c) => (
                <Tooltip key={c.label} title={c.label} placement="top" {...CELL_TOOLTIP}>
                  <span className={styles.legendCell} style={{ background: c.bg }} />
                </Tooltip>
              ))}
              <span className={styles.legendText}>More accurate</span>
            </div>
          </div>
        </div>

        {/* ── Grid (always 52 weeks, fills full width) ── */}
        <div className={styles.heatmapBody}>

          {/* Day labels */}
          <div className={styles.dayLabelsCol}>
            <div className={styles.monthSpacer} />
            {DAY_LABELS.map((label, i) => (
              <div key={i} className={styles.dayLabelCell}>{label}</div>
            ))}
          </div>

          {/* Week columns */}
          <div className={styles.weeksRow}>
            {weeks.map((week, wIdx) => (
              <div key={wIdx} className={styles.weekCol}>
                <div className={styles.monthLabelCell}>
                  {monthLabelMap[wIdx] ?? ""}
                </div>
                {week.map((day, dIdx) => {
                  const color = day.isFuture ? "transparent"
                    : getDayColor(day.quizCount, day.avgAccuracy);
                  const tooltipTitle = day.isFuture ? null : (
                    <span>
                      <strong style={{ display: "block", marginBottom: 4 }}>
                          {fmtDate(day.dateStr)}
                        </strong>
                      {day.quizCount
                        ? <>
                            {day.quizCount} quiz{day.quizCount !== 1 ? "zes" : ""}
                            <br />
                            Avg accuracy:{" "}
                            <strong style={{ color: getDayColor(day.quizCount, day.avgAccuracy) }}>
                              {day.avgAccuracy?.toFixed(1)}%
                            </strong>
                          </>
                        : <span style={{ color: "#94a3b8" }}>No activity</span>
                      }
                    </span>
                  );
                  return (
                    <Tooltip key={dIdx} title={tooltipTitle} placement="top" {...CELL_TOOLTIP}>
                      <div
                        className={`${styles.dayCell}
                          ${day.isFuture ? styles.dayCellFuture : ""}
                          ${!day.inRange && !day.isFuture ? styles.dayCellDimmed : ""}`}
                        style={{ background: color }}
                      />
                    </Tooltip>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* ── Stats strip ── */}
        <div className={styles.statsStrip}>
          <div className={`${styles.statChip} ${styles.statChipBlue}`}>
            <CalendarOutlined style={{ color: "#4b93ff", fontSize: 14 }} />
            <div className={styles.statContent}>
              <span className={styles.statLabel}>Active days</span>
              <span className={styles.statVal}>{stats.totalActive}</span>
            </div>
          </div>

          <div className={`${styles.statChip} ${styles.statChipPurple}`}>
            <ExperimentOutlined style={{ color: "#8b5cf6", fontSize: 14 }} />
            <div className={styles.statContent}>
              <span className={styles.statLabel}>Total quizzes</span>
              <span className={styles.statVal}>{stats.totalQuizzes}</span>
            </div>
          </div>

          <div className={`${styles.statChip} ${styles.statChipGreen}`}>
            <AimOutlined style={{ color: "#16a34a", fontSize: 14 }} />
            <div className={styles.statContent}>
              <span className={styles.statLabel}>Avg accuracy</span>
              <span className={`${styles.statVal} ${styles.statValGreen}`}>
                {stats.avgAccuracy != null ? `${stats.avgAccuracy}%` : "—"}
              </span>
            </div>
          </div>

          {stats.bestDay && (
            <div className={`${styles.statChip} ${styles.statChipAmber}`}>
              <TrophyOutlined style={{ color: "#d97706", fontSize: 14 }} />
              <div className={styles.statContent}>
                <span className={styles.statLabel}>Best day</span>
                <span className={styles.statVal}>
                  <span style={{ color: "#d97706" }}>
                    {stats.bestDay.avgAccuracy.toFixed(1)}%
                  </span>
                  <span className={styles.statSub}> · {fmtDate(stats.bestDay.date)}</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {isFetching && <div className={styles.shimmerOverlay} />}
      </div>
    </Card>
  );
};

export default DashboardActivityHeatmap;
